import * as Yup from 'yup';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@mui/lab';
import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormControlLabel,
  RadioGroup,
  FormLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { VehicleIcons } from 'utils/Icons/icons';

import { axios } from 'utils/axios';
import { FormHelperText } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Radio } from '@mui/material';
import { Typography } from '@mui/material';
import { Box } from '@mui/material';
import InfoCard from 'components/common/InfoCard';
import { CardHeader } from '@mui/material';

type VehicleFormProps = {
  isEdit: boolean;
  isDetails: boolean;
  currentUser?: any;
};

const NewVehicleSchema = Yup.object().shape({
  defaultDriver: Yup.string(),
  owner: Yup.string(),
  clientDefaultName: Yup.string(),
  haulier: Yup.string(),
  internalExternal: Yup.string(),
  vehicleRef: Yup.string(),
  councilArea: Yup.string(),
  registrationPlate: Yup.string()
    .required('Registration Plate is required')
    .transform((value) => value.replace(/\s+/g, '')),
  description: Yup.string().required('Description is required'),
  vehicleType: Yup.string().required('Vehicle Type is required'),
  vehicleCapacity: Yup.number().nullable(),
  tareWeight: Yup.number().nullable(),
  applytare: Yup.boolean(),
  useDefaultPurpose: Yup.boolean(),
  defaultPurposeDescription: Yup.string().when('useDefaultPurpose', {
    is: true,
    then: Yup.string().required('Default Purpose Description is required')
  }),
  currentStatus: Yup.string(),
  // sourcePopulate: Yup.boolean().required('Source Populate is required'),
  company: Yup.string().when('internalExternal', {
    is: 'External',
    then: Yup.string().required('Company is required for External users'),
    otherwise: Yup.string().nullable().notRequired()
  }),
  notes: Yup.string()
});

const NewVehicleEditSchema = Yup.object().shape({
  registrationPlate: Yup.string()
    .required('Registration Plate is required')
    .transform((value) => value.replace(/\s+/g, '')),
  vehicleRef: Yup.string(),
  vehicleType: Yup.string().required('Vehicle Type is required'),
  owner: Yup.string(),
  haulier: Yup.string(),
  description: Yup.string(),
  vehicleCapacity: Yup.string(),
  tareWeight: Yup.number(),
  notes: Yup.string()
});

export default function VehicleForm({ isEdit, isDetails, currentUser }: VehicleFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [vehicleTypes, setVehicleType] = useState<any>([]);
  const [company, setCompany] = useState<any>([]);
  const [contacts, setContacts] = useState<any>([]);
  const [client, setClient] = useState<any>([]);
  const [haulier, setHaulier] = useState<any>([]);
  const [drivers, setDrivers] = useState<any>([]);

  const [councils, setCouncils] = useState([]);

  // Formik setup
  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      defaultDriver: currentUser?.DefaultDriver || '',
      defaultDriverId: currentUser?.DefaultDriverId || '',
      owner: currentUser?.Owner || '',
      ownerId: currentUser?.OwnerId || '',
      clientDefaultName: currentUser?.ClientDefaultName || '',
      clientDefaultId: currentUser?.ClientDefaultId || '',
      haulier: currentUser?.Haulier || '',
      haulierId: currentUser?.HaulierId || '',
      internalExternal: currentUser?.InternalExternal || '',
      vehicleRef: currentUser?.VehicleRef || '',
      councilArea: currentUser?.CouncilArea || '',
      registrationPlate: currentUser?.RegistrationPlate?.replace(/\s+/g, '') || '',
      description: currentUser?.Description || '',
      vehicleType: currentUser?.VehicleType || '',
      vehicleTypeId: currentUser?.VehicleTypeId || '',
      vehicleCapacity: currentUser?.VehicleCapacity || '',
      tareWeight: currentUser?.TareWeight || '',
      applytare: currentUser?.ApplyTare || false,
      useDefaultPurpose: currentUser?.UseDefaultPurpose || false,
      defaultPurposeDescription: currentUser?.DefaultPurposeDescription || '',
      currentStatus: currentUser?.CurrentStatus || '',
      // company: data.find((el: any) => el.id === currentUser.CompanyID)?.value || '',
      company: currentUser?.defaultCompanyId || '',
      companyId: currentUser?.CompanyID || '',
      notes: currentUser?.Notes || ''
    },
    validationSchema: isEdit
      ? NewVehicleEditSchema
      : !isEdit && !isDetails
      ? NewVehicleSchema
      : null,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          DefaultDriver: values.defaultDriver,
          DefaultDriverId: values.defaultDriverId,
          Owner: values.owner,
          OwnerId: values.ownerId,
          ClientDefaultName: values.clientDefaultName,
          ClientDefaultId: values.clientDefaultId,
          Haulier: values.haulier,
          HaulierId: values.haulierId,
          InternalExternal: values.internalExternal,
          VehicleRef: values.vehicleRef,
          CouncilArea: values.councilArea,
          RegistrationPlate: values.registrationPlate.replace(/\s+/g, ''),
          Description: values.description,
          VehicleType: values.vehicleType,
          VehicleTypeId: values?.vehicleTypeId,
          VehicleCapacity: values.vehicleCapacity,
          TareWeight: values.tareWeight,
          ApplyTare: values.applytare,
          UseDefaultPurpose: values.useDefaultPurpose,
          DefaultPurposeDescription: values.defaultPurposeDescription,
          CurrentStatus: values.currentStatus,
          defaultCompanyId: values.company,
          CompanyID: values.companyId,
          Notes: values.notes
        };

        if (!isEdit) {
          const response = await axios.post('/rest/api/vehicle', payload);
        } else {
          const response = await axios.put(`/rest/api/vehicle/${currentUser.Id}`, payload);
        }

        enqueueSnackbar(!isEdit ? 'Create success' : 'Update success', { variant: 'success' });
        navigate('/dashboard/vehicles');
      } catch (error) {
        console.error(error);
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  const fetchAllCouncils = async () => {
    const response = await axios.get('/rest/api/council/area');
    const allCouncils = response.data.data || [];
    setCouncils(allCouncils?.map((el: any) => ({ value: el.CouncilArea, id: el.Id })));
  };

  const fetchAllVehicleTypes = async () => {
    const response = await axios.get('/rest/api/vehicle-type');
    const allVehicleTypes = response.data.data.data.records || [];
    setVehicleType(
      allVehicleTypes?.map((el: any) => ({ value: el.VEHICLE_TYPE, id: el.VEHICLE_TYPE_ID }))
    );
  };
  const fetchContacts = async () => {
    try {
      const response1 = await axios.get('/rest/api/contacts/dropdown?isOwner=1');
      const response2 = await axios.get('/rest/api/contacts/dropdown?isClient=1');
      const response3 = await axios.get('/rest/api/contacts/dropdown?IsHaulier=1');
      const response4 = await axios.get('/rest/api/company/dropdown');
      const Companies = response4.data.data || [];
      const allContacts = response1.data.data || [];
      const allClients = response2.data.data || [];
      const allHaulier = response3.data.data || [];
      setCompany(Companies.map((el: any) => ({ value: el.name, id: el.id })));
      setContacts(
        allContacts.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
      setClient(
        allClients.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
      setHaulier(
        allHaulier.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchDrivers = useCallback(async () => {
    try {
      const response = await axios.get('/rest/api/drivers');
      const allDrivers = response.data.data || [];
      setDrivers(allDrivers.map((el: any) => ({ value: el.FullName, id: el.Id })));
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, []);

  useEffect(() => {
    fetchAllCouncils();
    fetchAllVehicleTypes();
    fetchContacts();
    fetchDrivers();
  }, []);

  return (
    <>
      {isDetails ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3, boxShadow: 3 }}>
              {/* Vehicle Information Section */}
              <CardHeader title="Vehicle Information" />
              <Stack spacing={3} padding={3}>
                <Grid container spacing={2}>
                  {[
                    {
                      icon: VehicleIcons.carRepair,
                      title: 'Registration Plate No',
                      content: formik.values.registrationPlate
                    },
                    {
                      icon: VehicleIcons.accountBox,
                      title: 'Vehicle Ref No',
                      content: formik.values.vehicleRef
                    },
                    {
                      icon: VehicleIcons.description,
                      title: 'Description',
                      content: formik.values.description
                    },
                    {
                      icon: VehicleIcons.directionsCar,
                      title: 'Vehicle Type',
                      content: formik.values.vehicleType
                    },
                    {
                      icon: VehicleIcons.build,
                      title: 'Vehicle Capacity',
                      content: formik.values.vehicleCapacity
                    },
                    {
                      icon: VehicleIcons.work,
                      title: 'Tare Weight',
                      content: formik.values.tareWeight
                    }
                  ].map(({ icon, title, content }, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <InfoCard icon={icon} title={title} content={content} />
                    </Grid>
                  ))}
                </Grid>
              </Stack>

              {/* Ownership Information Section */}
              <CardHeader title="Ownership Information" />
              <Stack spacing={3} padding={3}>
                <Grid container spacing={2}>
                  {[
                    {
                      icon: VehicleIcons.accountCircle,
                      title: 'Owner Name',
                      content: formik.values.owner
                    },
                    {
                      icon: VehicleIcons.home,
                      title: 'Client Default Name',
                      content: formik.values.clientDefaultName
                    },
                    {
                      icon: VehicleIcons.locationOn,
                      title: 'Haulier',
                      content: formik.values.haulier
                    }
                  ].map(({ icon, title, content }, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <InfoCard icon={icon} title={title} content={content} />
                    </Grid>
                  ))}
                </Grid>
              </Stack>

              {/* Additional Settings Section */}
              <CardHeader title="Additional Settings" />
              <Stack spacing={3} padding={3}>
                <Grid container spacing={2}>
                  {[
                    {
                      icon: VehicleIcons.settings,
                      title: 'Apply Tare',
                      content: formik.values.applytare ? 'Yes' : 'No'
                    },
                    {
                      icon: VehicleIcons.public,
                      title: 'Use Default Purpose',
                      content: formik.values.useDefaultPurpose ? 'Yes' : 'No'
                    },
                    {
                      icon: VehicleIcons.noteAlt,
                      title: 'Notes',
                      content: formik.values.notes,
                      fullWidth: true
                    }
                  ].map(({ icon, title, content, fullWidth }, idx) => (
                    <Grid item xs={12} sm={fullWidth ? 12 : 6} key={idx}>
                      <InfoCard icon={icon} title={title} content={content} />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <FormikProvider value={formik}>
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    {/* Vehicle Details Group */}
                    <legend style={{ fontWeight: 'bold' }}>Vehicle Information</legend>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label="Registration Plate No*"
                        {...getFieldProps('registrationPlate')}
                        error={Boolean(touched.registrationPlate && errors.registrationPlate)}
                        helperText={touched.registrationPlate && errors.registrationPlate}
                      />
                      <TextField
                        fullWidth
                        label="Description*"
                        {...getFieldProps('description')}
                        error={Boolean(touched.description && errors.description)}
                        helperText={touched.description && errors.description}
                      />
                      <FormControl fullWidth>
                        <Autocomplete
                          options={vehicleTypes}
                          getOptionLabel={(option: { value: string; id: number }) => option.value}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          value={
                            vehicleTypes?.find(
                              (option: { value: string; id: number }) =>
                                option.id === formik.values.vehicleTypeId
                            ) || null
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                              </div>
                            </li>
                          )}
                          onChange={(event, newValue) => {
                            formik.setFieldValue('vehicleType', newValue ? newValue.value : '');
                            formik.setFieldValue('vehicleTypeId', newValue ? newValue.id : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Vehicle Type*"
                              error={Boolean(touched.vehicleType && errors.vehicleType)}
                              helperText={touched.vehicleType && errors.vehicleType}
                            />
                          )}
                        />
                      </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label="Vehicle Ref No"
                        {...getFieldProps('vehicleRef')}
                        error={Boolean(touched.vehicleRef && errors.vehicleRef)}
                        helperText={touched.vehicleRef && errors.vehicleRef}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Vehicle Capacity"
                        {...getFieldProps('vehicleCapacity')}
                        error={Boolean(touched.vehicleCapacity && errors.vehicleCapacity)}
                        helperText={touched.vehicleCapacity && errors.vehicleCapacity}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Tare Weight"
                        {...getFieldProps('tareWeight')}
                        error={Boolean(touched.tareWeight && errors.tareWeight)}
                        helperText={touched.tareWeight && errors.tareWeight}
                      />
                    </Stack>

                    {/* Ownership Details Group */}
                    <legend style={{ fontWeight: 'bold' }}>Ownership Information</legend>
                    <Stack direction="row" spacing={2}>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={contacts}
                          getOptionLabel={(option: {
                            value: string;
                            id: number;
                            accountRef?: string;
                          }) => option.value}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={
                            contacts?.find(
                              (option: { value: string; id: number }) =>
                                option.id === formik.values.ownerId
                            ) || null
                          }
                          filterOptions={(options, { inputValue }) =>
                            options.filter(
                              (option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase()) ||
                                option.accountRef?.toLowerCase().includes(inputValue.toLowerCase())
                            )
                          }
                          renderOption={(props, option) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option.value || 'N/A'}</strong>
                                <br />
                                <small
                                  style={{ fontSize: '0.675rem', fontWeight: 400, color: 'gray' }}
                                >
                                  {option.accountRef || 'N/A'}
                                </small>
                              </div>
                            </li>
                          )}
                          onChange={(event, newValue) => {
                            formik.setFieldValue('owner', newValue?.value || '');
                            formik.setFieldValue('ownerId', newValue?.id || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Owner"
                              error={Boolean(touched.owner && errors.owner)}
                              helperText={touched.owner && errors.owner}
                            />
                          )}
                        />
                        {formik.values.ownerId && (
                          <Typography variant="body2" style={{ color: 'gray', marginTop: 4 }}>
                            {contacts.find((c: any) => c.id === formik.values.ownerId)
                              ?.accountRef || 'N/A'}
                          </Typography>
                        )}
                      </FormControl>

                      <FormControl fullWidth>
                        <Autocomplete
                          options={client}
                          getOptionLabel={(option: { value: string; id: number }) => option.value}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={
                            client?.find(
                              (option: { value: string; id: number }) =>
                                option.id === formik.values.clientDefaultId
                            ) || null
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                                <br />
                                <small
                                  style={{ fontSize: '0.675rem', fontWeight: 400, color: 'gray' }}
                                >
                                  {option?.accountRef || 'N/A'}
                                </small>
                              </div>
                            </li>
                          )}
                          onChange={(event, newValue) => {
                            formik.setFieldValue(
                              'clientDefaultName',
                              newValue ? newValue.value : ''
                            );
                            formik.setFieldValue('clientDefaultId', newValue ? newValue.id : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Client"
                              error={Boolean(touched.clientDefaultName && errors.clientDefaultName)}
                              helperText={touched.clientDefaultName && errors.clientDefaultName}
                            />
                          )}
                        />
                        {formik.values.clientDefaultId && (
                          <Typography variant="body2" style={{ color: 'gray', marginTop: 4 }}>
                            {client.find((c: any) => c.id === formik.values.clientDefaultId)
                              ?.accountRef || 'N/A'}
                          </Typography>
                        )}
                      </FormControl>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={haulier}
                          getOptionLabel={(option: { value: string; id: number }) => option.value}
                          isOptionEqualToValue={(option, value) => option.id === value.id} // Ensures correct selection
                          value={
                            haulier?.find(
                              (option: { value: string; id: number }) =>
                                option.id === formik.values.haulierId
                            ) || null
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                                <br />
                                <small
                                  style={{ fontSize: '0.675rem', fontWeight: 400, color: 'gray' }}
                                >
                                  {option?.accountRef || 'N/A'}
                                </small>
                              </div>
                            </li>
                          )}
                          onChange={(event, newValue) => {
                            formik.setFieldValue('haulier', newValue ? newValue.value : '');
                            formik.setFieldValue('haulierId', newValue ? newValue.id : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Haulier"
                              error={Boolean(touched.haulier && errors.haulier)}
                              helperText={touched.haulier && errors.haulier}
                            />
                          )}
                        />
                        {formik.values.haulierId && (
                          <Typography variant="body2" style={{ color: 'gray', marginTop: 4 }}>
                            {haulier.find((c: any) => c.id === formik.values.haulierId)
                              ?.accountRef || 'N/A'}
                          </Typography>
                        )}
                      </FormControl>
                    </Stack>

                    {/* Operational Details Group */}
                    <legend style={{ fontWeight: 'bold' }}>Operational Details</legend>
                    <Stack direction="row" spacing={2}>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={drivers}
                          getOptionLabel={(option: { value: string; id: number }) => option.value}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          value={
                            drivers?.find(
                              (option: { value: string; id: number }) =>
                                option.id === formik.values.defaultDriverId
                            ) || null
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                              </div>
                            </li>
                          )}
                          onChange={(event, newValue) => {
                            formik.setFieldValue('defaultDriver', newValue ? newValue.value : '');
                            formik.setFieldValue('defaultDriverId', newValue ? newValue.id : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Default Driver"
                              error={Boolean(touched.defaultDriver && errors.defaultDriver)}
                              helperText={touched.defaultDriver && errors.defaultDriver}
                            />
                          )}
                        />
                      </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <FormControl
                        sx={{
                          width: '100%'
                        }}
                      >
                        <InputLabel>Internal/External</InputLabel>
                        <Select
                          name="internalExternal"
                          value={formik.values.internalExternal}
                          onChange={formik.handleChange}
                          label="Internal/External"
                        >
                          <MenuItem value="Internal">Internal</MenuItem>
                          <MenuItem value="External">External</MenuItem>
                        </Select>
                        <FormHelperText>
                          {touched.internalExternal && errors.internalExternal}
                        </FormHelperText>
                      </FormControl>
                      <FormControl
                        sx={{
                          width: '100%'
                        }}
                      >
                        <InputLabel>Current Status</InputLabel>
                        <Select
                          name="currentStatus"
                          value={formik.values.currentStatus}
                          onChange={formik.handleChange}
                          label="Current Status"
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="InActive">InActive</MenuItem>
                        </Select>
                        <FormHelperText>
                          {touched.currentStatus && errors.currentStatus}
                        </FormHelperText>
                      </FormControl>
                    </Stack>
                    {/* Location Details Group */}
                    <legend style={{ fontWeight: 'bold' }}>Location Details</legend>
                    <Stack direction="row" spacing={2}>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={councils}
                          getOptionLabel={(option: { value: string; id: number }) => option.value}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                              </div>
                            </li>
                          )}
                          onChange={(event, newValue) => {
                            formik.setFieldValue('councilArea', newValue ? newValue.value : '');
                          }}
                          value={
                            councils?.find(
                              (option: { value: string; id: number }) =>
                                option.value === formik.values.councilArea
                            ) || null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Council Area"
                              error={Boolean(touched.councilArea && errors.councilArea)}
                              helperText={touched.councilArea && errors.councilArea}
                            />
                          )}
                        />
                      </FormControl>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={company}
                          getOptionLabel={(option: { value: string; id: number }) => option.value}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                                <br />
                              </div>
                            </li>
                          )}
                          value={
                            company?.find(
                              (option: { value: string; id: number }) =>
                                option.id === formik.values.company
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            formik.setFieldValue('company', newValue ? newValue.id : '');
                            formik.setFieldValue('companyId', newValue ? newValue.id : null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Company"
                              required={formik.values.internalExternal === 'External'}
                              error={Boolean(touched.company && errors.company)}
                              helperText={touched.company && errors.company}
                            />
                          )}
                        />
                      </FormControl>
                    </Stack>

                    {/* Additional Settings */}
                    <legend style={{ fontWeight: 'bold' }}>Additional Settings</legend>
                    <Stack direction="row" spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.applytare || false}
                            onChange={formik.handleChange}
                            name="applytare"
                          />
                        }
                        label="Apply Tare"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.useDefaultPurpose}
                            onChange={formik.handleChange}
                            name="useDefaultPurpose"
                          />
                        }
                        label="Use Default Purpose"
                      />
                    </Stack>
                    {formik.values.useDefaultPurpose && (
                      <TextField
                        fullWidth
                        label="Default Purpose Description"
                        {...getFieldProps('defaultPurposeDescription')}
                        error={Boolean(
                          touched.defaultPurposeDescription && errors.defaultPurposeDescription
                        )}
                        helperText={
                          touched.defaultPurposeDescription && errors.defaultPurposeDescription
                        }
                      />
                    )}
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={4} // Adjust the number of rows as needed
                      {...getFieldProps('notes')}
                      error={Boolean(touched.notes && errors.notes)}
                      helperText={touched.notes && errors.notes}
                    />

                    {/* Submit Button */}
                    {!isDetails && (
                      <Stack direction="row" justifyContent="flex-end" spacing={2}>
                        <LoadingButton
                          type="submit"
                          variant="contained"
                          loading={isSubmitting}
                          disabled={
                            !isEdit &&
                            ((!isDetails && !formik.isValid) || // If validation is required and form is invalid
                              formik.isSubmitting ||
                              Object.keys(formik.errors).length > 0)
                          }
                        >
                          {isEdit ? 'Update Vehicle' : 'Create Vehicle'}
                        </LoadingButton>
                      </Stack>
                    )}
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      )}
    </>
  );
}
